import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { TOP_SELLERS_CATEGORY_URL_ID } from "@faire/retailer-shared/categories/urlId";
import { optimizeImageUrlFillSquare } from "@faire/web--source/common/images/optimizeImages";
import { formatMoney, RoundingStrategy, selectLocalCurrency } from "@faire/web--source/common/money";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Flex } from "@faire/web--source/slate/Layout";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api--source/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api--source/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ISO3166Alpha3 } from "@faire/web-api--source/indigofair/iso3166/ISO3166Alpha3";
import categoryPath from "@faire/web-api--source/routes/category/c1CategoryUrlId";
import React, { useMemo } from "react";
import { getSessionCountry } from "@faire/retailer-visitor-shared/serialized-data/getSessionCountry";
import { useSettingLoggedOutHomepageRedesignV2 } from "@faire/retailer/settings/useSettingLoggedOutHomepageRedesignV2";
import * as componentStyles from "./styles.css";
var BESTSELLERS_BANNER_IMAGES = [
    "https://cdn.faire.com/fastly/2bc4d97018ef06e79731c8fbbfa201b4a440837c450a5725d323c344f4676645.jpeg",
    "https://cdn.faire.com/fastly/f28fda81e75209633f57367c1f0a46f1fe6148b0001dc50ca12e751aa2ae9811.jpeg",
    "https://cdn.faire.com/fastly/4fa9be357b08c6ee07517a66256380f0315bf38ceb05c0bf969dff9f548c4b0c.jpeg",
    "https://cdn.faire.com/fastly/9d389feb08a0d51bb991417050e2e120cf536e20785109edecf87fa7c7758b73.jpeg",
    "https://cdn.faire.com/fastly/711f60feb020f139346b5cd15b69422bc09f380e80d2bfdbccfd67a8c42794cb.jpeg",
    "https://cdn.faire.com/fastly/6ff47a25ba068d16f90a2c5f5227f20d6600dfb782c4f749a8554915adcf0482.jpeg"
];
export var BestsellersBanner = function() {
    var homepageRedesignVariant = useSettingLoggedOutHomepageRedesignV2();
    var ref = useDoActionWhenVisible(function() {
        return trackLoggedOutRetailersLoggedOutHomeRedesignView("bestsellers_banner", "".concat(homepageRedesignVariant));
    });
    return /*#__PURE__*/ _jsxs(Flex, {
        direction: {
            mobileAndAbove: "column",
            tabletAndAbove: "row"
        },
        gap: {
            mobileAndAbove: "4x",
            tabletAndAbove: "5x"
        },
        className: componentStyles.bestsellersBannerContainer,
        "data-test-id": "bestsellers-banner",
        ref: ref,
        children: [
            /*#__PURE__*/ _jsx(ResponsiveContentV2, {
                desktop: /*#__PURE__*/ _jsx(BestsellersCTA, {}),
                tabletAndBelow: /*#__PURE__*/ _jsx(BestsellersCTA, {})
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: componentStyles.bestsellersGrid,
                children: [
                    /*#__PURE__*/ _jsx(ResponsiveContentV2, {
                        xLargeAndAbove: /*#__PURE__*/ _jsx(BestsellersCTA, {})
                    }),
                    BESTSELLERS_BANNER_IMAGES.map(function(imageUrl, index) {
                        return /*#__PURE__*/ _jsx(BestsellersImage, {
                            imageUrl: imageUrl,
                            index: index
                        }, index);
                    })
                ]
            })
        ]
    });
};
var BestsellersCTA = function() {
    var homepageRedesignVariant = useSettingLoggedOutHomepageRedesignV2();
    var sessionCountry = getSessionCountry();
    var headerIncentive = useMemo(function() {
        return formatMoney({
            money: IFaireMoney.build({
                amount_cents: 10000,
                currency: selectLocalCurrency(sessionCountry !== null && sessionCountry !== void 0 ? sessionCountry : ISO3166Alpha3.USA)
            }),
            roundingStrategy: RoundingStrategy.DROP_CENTS,
            disableAutoConvertCurrency: true
        });
    }, [
        sessionCountry
    ]);
    return /*#__PURE__*/ _jsxs(Column, {
        gap: "2x",
        className: componentStyles.bestsellersCTAContainer,
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                variant: {
                    mobileAndAbove: "displaySSerifRegular",
                    desktopAndAbove: "displayMSerifRegular"
                },
                color: Core.text.inverse,
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "diDnen",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Discover your next bestseller"
                        }
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Typography, {
                variant: "subheadingSansRegular",
                color: Core.text.inverse,
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "+JH49z",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "See what retailers from across the globe are loving on Faire"
                        }
                    ],
                    values: {
                        headerIncentiveMoney: headerIncentive
                    }
                })
            }),
            /*#__PURE__*/ _jsx(Button, {
                style: {
                    width: "fit-content"
                },
                variant: "secondary",
                linkTo: categoryPath(TOP_SELLERS_CATEGORY_URL_ID),
                className: componentStyles.bestsellersCTA,
                onClick: function() {
                    trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick("bestsellers_banner-cta", categoryPath(TOP_SELLERS_CATEGORY_URL_ID), "".concat(homepageRedesignVariant));
                },
                children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                    id: "LddrAD",
                    defaultMessage: [
                        {
                            type: 0,
                            value: "Shop all bestsellers"
                        }
                    ]
                })
            })
        ]
    });
};
var BestsellersImage = function(param) {
    var imageUrl = param.imageUrl, index = param.index;
    var isSSR = useIsSSR();
    var optimizedImageUrl = optimizeImageUrlFillSquare(IImage.build({
        url: imageUrl
    }), isSSR, 260);
    return /*#__PURE__*/ _jsx("img", {
        src: optimizedImageUrl,
        loading: "lazy",
        style: {
            gridArea: "img".concat(index),
            height: "100%",
            width: "100%"
        },
        "data-index": index,
        className: componentStyles.bestsellersImage
    });
};
